import {Modal} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import type {ReactNode} from "react";
import {StandardModalHeader} from "~/components/standardModalHeader";

const ROW_ENGAGEMENT_MODAL_MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

export function RowEngagementModal({
    opened,
    title,
    onClose,
    children,
    footer,
    contentWrapper,
    centered = false,
    fullScreenOnMobile = true,
    preventCloseWhileLoading = false,
    isLoading = false,
}: {
    opened: boolean;
    title: ReactNode;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /**
     * Wraps body + footer inside the modal portal so React context
     * (e.g. form values) is available to both.
     */
    contentWrapper?: (content: ReactNode) => ReactNode;
    centered?: boolean;
    fullScreenOnMobile?: boolean;
    preventCloseWhileLoading?: boolean;
    isLoading?: boolean;
}) {
    const isMobile = useMediaQuery(ROW_ENGAGEMENT_MODAL_MOBILE_BREAKPOINT, false, {
        getInitialValueInEffect: true,
    });
    const useFullScreen = fullScreenOnMobile === true && isMobile === true;

    const handleClose = () => {
        if (preventCloseWhileLoading === true && isLoading === true) {
            return;
        }
        onClose();
    };

    const desktopModalClassNames = {
        inner: "!items-start !pt-[60px] !justify-center",
        content: "bg-ds-elevation-surface-current! !w-[37.5rem] !max-w-[37.5rem]",
        header: "!w-full border-b border-ds-border !px-ds-space-200 !py-ds-space-100",
        title: "!w-full !flex-1 !mr-0",
        body: footer !== undefined ? "!p-0 !flex !flex-col !overflow-hidden" : "!p-ds-space-200",
    };

    const inner = (
        <div
            className={
                useFullScreen === true || footer !== undefined
                    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                    : "grid gap-y-ds-space-200"
            }
        >
            <div
                className={
                    useFullScreen === true
                        ? "min-h-0 flex-1 overflow-y-auto px-ds-space-200 pt-ds-space-200 pb-ds-space-200"
                        : footer !== undefined
                          ? "min-h-0 flex-1 overflow-y-auto px-ds-space-200 pt-ds-space-200 pb-ds-space-200"
                          : undefined
                }
            >
                {children}
            </div>
            {footer !== undefined ? (
                <div
                    className={
                        useFullScreen === true
                            ? "flex shrink-0 items-center justify-end gap-x-ds-space-075 border-t border-ds-border bg-ds-elevation-surface-current px-ds-space-200 pt-ds-space-100 pb-ds-space-200"
                            : "flex shrink-0 items-center justify-end gap-x-ds-space-075 border-t border-ds-border px-ds-space-200 pt-ds-space-100 pb-ds-space-200"
                    }
                >
                    {footer}
                </div>
            ) : null}
        </div>
    );

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered={useFullScreen !== true && centered === true}
            fullScreen={useFullScreen}
            withCloseButton={false}
            classNames={
                useFullScreen === true
                    ? {
                          inner: "p-0!",
                          content:
                              "!w-full !h-full !max-w-none !flex !flex-col bg-ds-elevation-surface-current! rounded-none!",
                          header: "!shrink-0 !w-full !px-ds-space-200 !pt-ds-space-200 !pb-ds-space-100 border-b border-ds-border",
                          title: "!w-full !flex-1 !mr-0",
                          body: "!flex !min-h-0 !flex-1 !flex-col !p-0 !overflow-hidden",
                      }
                    : desktopModalClassNames
            }
            title={
                <StandardModalHeader
                    title={title}
                    onClose={handleClose}
                    closeDisabled={preventCloseWhileLoading === true && isLoading === true}
                    className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-x-ds-space-100 px-0 py-0"
                />
            }
        >
            {contentWrapper === undefined ? inner : contentWrapper(inner)}
        </Modal>
    );
}
